import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaQrcode, FaShareAlt, FaMobileAlt, FaCheckCircle, 
  FaCheck, FaTimes, FaCrown, FaBuilding 
} from 'react-icons/fa';
import './Landing.css';

const Landing = () => {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', company: '', message: '' });

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement lead submission to backend
    alert('Thank you for your interest! We will contact you soon.');
    setShowLeadForm(false);
    setLeadData({ name: '', email: '', company: '', message: '' });
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Create Your Digital Business Card</h1>
            <p className="hero-subtitle">
              Generate a QR code for your vCard and share your contact information instantly. 
              Perfect for networking events, conferences, and business meetings.
            </p>
            <div className="hero-buttons">
              <Link to="/vcard/create" className="btn btn-primary btn-large">
                Create Your vCard
              </Link>
              <Link to="/login" className="btn btn-secondary btn-large">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-icon">
                <FaQrcode />
              </div>
              <h3>Step 1: Create Your vCard</h3>
              <p>Fill in your contact details, social media links, and customize your design with colors and images.</p>
            </div>
            <div className="step">
              <div className="step-icon">
                <FaShareAlt />
              </div>
              <h3>Step 2: Generate QR Code</h3>
              <p>Get your unique QR code that links to your digital business card. Share it anywhere!</p>
            </div>
            <div className="step">
              <div className="step-icon">
                <FaMobileAlt />
              </div>
              <h3>Step 3: Share & Connect</h3>
              <p>People scan your QR code and instantly get your contact information. They can save it directly to their phone.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="section-title">Why Choose Our vCard Platform?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <FaCheckCircle className="benefit-icon" />
              <h3>Easy to Use</h3>
              <p>Create your vCard in minutes, no technical skills required.</p>
            </div>
            <div className="benefit-card">
              <FaCheckCircle className="benefit-icon" />
              <h3>Fully Customizable</h3>
              <p>Choose colors, add images, and personalize your card to match your brand.</p>
            </div>
            <div className="benefit-card">
              <FaCheckCircle className="benefit-icon" />
              <h3>Multiple Contact Methods</h3>
              <p>Add multiple phone numbers, emails, and social media profiles.</p>
            </div>
            <div className="benefit-card">
              <FaCheckCircle className="benefit-icon" />
              <h3>Physical Cards Available</h3>
              <p>Order printed business cards with your QR code for professional networking.</p>
            </div>
            <div className="benefit-card">
              <FaCheckCircle className="benefit-icon" />
              <h3>Instant Sharing</h3>
              <p>Share your contact info instantly - no more fumbling with business cards.</p>
            </div>
            <div className="benefit-card">
              <FaCheckCircle className="benefit-icon" />
              <h3>Always Up-to-Date</h3>
              <p>Update your information anytime, and your QR code stays the same.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="section-title">Choose Your Plan</h2>
          <p className="pricing-subtitle">Select the perfect plan for your needs</p>
          <div className="pricing-grid">
            {/* Free Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3 className="plan-name">Free</h3>
                <div className="plan-price">
                  <span className="price-amount">$0</span>
                  <span className="price-period">/month</span>
                </div>
                <p className="plan-description">Perfect for getting started</p>
              </div>
              <div className="pricing-features">
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>3 vCards</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>2 Services</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Basic QR Code</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Profile Image</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Social Media Links</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>5MB File Upload</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Basic Analytics</span>
                </div>
                <div className="feature-item disabled">
                  <FaTimes className="times-icon" />
                  <span>Products</span>
                </div>
                <div className="feature-item disabled">
                  <FaTimes className="times-icon" />
                  <span>Brochures/Flipbook</span>
                </div>
                <div className="feature-item disabled">
                  <FaTimes className="times-icon" />
                  <span>Custom Domain</span>
                </div>
                <div className="feature-item disabled">
                  <FaTimes className="times-icon" />
                  <span>White-label</span>
                </div>
              </div>
              <Link to="/register" className="btn btn-outline btn-block">
                Get Started Free
              </Link>
            </div>

            {/* Premium Plan */}
            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <div className="limited-offer-badge">Limited Time Offer</div>
              <div className="pricing-header">
                <h3 className="plan-name">
                  <FaCrown className="crown-icon" /> Premium
                </h3>
                <div className="plan-price">
                  <span className="price-amount">$15</span>
                  <span className="price-period">/year</span>
                </div>
                <p className="plan-description">Best for professionals and small businesses</p>
              </div>
              <div className="pricing-features">
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>10 vCards</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>10 Services</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Advanced QR Code</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Featured Services</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Unlimited Products</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Unlimited Brochures</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Flipbook Viewer</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>50MB File Upload</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Detailed Analytics</span>
                </div>
                <div className="feature-item disabled">
                  <FaTimes className="times-icon" />
                  <span>Custom Domain</span>
                </div>
                <div className="feature-item disabled">
                  <FaTimes className="times-icon" />
                  <span>White-label</span>
                </div>
              </div>
              <Link to="/register" className="btn btn-primary btn-block">
                Upgrade to Premium
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className="pricing-card enterprise">
              <div className="pricing-header">
                <h3 className="plan-name">
                  <FaBuilding className="building-icon" /> Enterprise
                </h3>
                <div className="plan-price">
                  <span className="price-amount">Custom</span>
                </div>
                <p className="plan-description">For large organizations and agencies</p>
              </div>
              <div className="pricing-features">
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Everything in Premium</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Unlimited Services</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Unlimited Everything</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Custom Domain</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>SSL Certificate (Auto)</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>White-label Options</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Advanced Analytics</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Visitor Tracking</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Geographic Analytics</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Export Analytics</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Priority Support</span>
                </div>
                <div className="feature-item">
                  <FaCheck className="check-icon" />
                  <span>Onboarding Assistance</span>
                </div>
              </div>
              <button 
                onClick={() => setShowLeadForm(true)} 
                className="btn btn-enterprise btn-block"
              >
                Get a Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="lead-modal-overlay" onClick={() => setShowLeadForm(false)}>
          <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setShowLeadForm(false)}>×</button>
            <h3>Request Enterprise Quote</h3>
            <form onSubmit={handleLeadSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={leadData.name}
                  onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={leadData.email}
                  onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Company</label>
                <input
                  type="text"
                  value={leadData.company}
                  onChange={(e) => setLeadData({ ...leadData, company: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea
                  rows="4"
                  value={leadData.message}
                  onChange={(e) => setLeadData({ ...leadData, message: e.target.value })}
                  placeholder="Tell us about your requirements..."
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block">
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Create Your Digital Business Card?</h2>
          <p>Start building your vCard now - no account required to begin!</p>
          <Link to="/vcard/create" className="btn btn-primary btn-large">
            Get Started Free
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Landing;

